import cc from 'classcat';
import { Dispatch, SetStateAction } from 'react';
import { DOTS, usePagination, Dropdown, IMenuItem } from '../../Common';

export interface IPaginationProps {
    total: number;
    elementsPerPage: number;
    setElementsPerPage?: Dispatch<SetStateAction<number>>;
    page: number;
    setPage?: Dispatch<SetStateAction<number>>;
    onPageChange?: (page: number) => void;
    isClientPage?: boolean;
}

const previousPageCalculation = (page: number) => (page - 1 !== -1 ? page - 1 : page);
const nextPageCalculation = (page: number, total: number, elementsPerPage: number) => {
    const totalPages = Math.ceil(total / elementsPerPage);
    return page + 1 < totalPages ? page + 1 : page;
};

export const Pagination = ({ total, elementsPerPage, setElementsPerPage, page, setPage, onPageChange, isClientPage }: IPaginationProps) => {
    const paginate = (pageNumber: number) => {
        const newPage = pageNumber - 1;
        setPage?.(newPage);
        onPageChange?.(newPage);
    };

    const previousPage = () => {
        if (page > 0) {
            setPage?.(page => previousPageCalculation(page));
            onPageChange?.(previousPageCalculation(page));
        }
    };

    const nextPage = () => {
        const totalPages = Math.ceil(total / elementsPerPage);
        if (page + 1 < totalPages) {
            setPage?.(page => nextPageCalculation(page, total, elementsPerPage));
            onPageChange?.(nextPageCalculation(page, total, elementsPerPage));
        }
    };

    const isLastPage = page + 1 >= Math.ceil(total / elementsPerPage);
    const isFirstPage = page === 0;

    const pages = usePagination({
        currentPage: page + 1,
        total,
        elementsPerPage,
    });

    const perPageItems: IMenuItem[] = [
        { value: 10, label: '10' },
        { value: 25, label: '25' },
        { value: 50, label: '50' },
        { value: 100, label: '100' },
    ];

    const handleChangePerPage = (item: IMenuItem) => {
        setPage?.(0);
        setElementsPerPage?.(Number(item.value));
    };

    return (
        <>
            <div className={cc(['pagination', isClientPage && 'pagination--client-page'])}>
                {!isClientPage && (
                    <div className="pagination__rows">
                        {total > Number(perPageItems[0].value) && (
                            <>
                                <p>Rows per page</p>
                                <Dropdown
                                    className="pagination__per-page"
                                    menuItems={perPageItems}
                                    selected={perPageItems.find(item => item.value === elementsPerPage) || perPageItems[0]}
                                    onChange={item => handleChangePerPage(item)}
                                    isOpenAbove={true}
                                />
                            </>
                        )}
                    </div>
                )}

                <div className="pagination__right">
                    <div className="pagination__pages">
                        <button
                            className={cc(['pagination__nav-button', { 'pagination__nav-button--disabled': isFirstPage }])}
                            onClick={previousPage}
                            disabled={isFirstPage}
                        >
                            Prev
                        </button>
                        {pages?.map(_page => {
                            if (_page === DOTS) {
                                return (
                                    <button key={_page} className="pagination__list-item">
                                        &#8230;
                                    </button>
                                );
                            }

                            return (
                                <button
                                    key={_page}
                                    onClick={() => paginate(parseInt(_page.toString()))}
                                    className={cc(['pagination__list-item', { 'pagination__active-page': _page === page + 1 }])}
                                >
                                    {_page}
                                </button>
                            );
                        })}
                        <button
                            className={cc(['pagination__nav-button', { 'pagination__nav-button--disabled': isLastPage }])}
                            onClick={nextPage}
                            disabled={isLastPage}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
